import { apiPost } from '@/services/api/client';

export type OcrConfidence = 'low' | 'medium' | 'high';

export type OcrRamqResult = {
  ramq: string;
  firstName?: string;
  lastName?: string;
  confidence: OcrConfidence;
  message?: string;
};

// Backend rejects payloads whose decoded base64 exceeds 512 KB
// (handler._validate_base64_image). Leave headroom so we never bump the cap.
const MAX_DECODED_BYTES = 460 * 1024;
const MAX_DIMENSION = 1024;
const QUALITY_STEPS = [0.6, 0.45, 0.3];

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      'image/jpeg',
      quality,
    );
  });
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
}

// Mirrors Flutter's _prepareImageForUpload: resize so max dimension is
// 1024 px, then JPEG-encode. Backend caps decoded bytes at 512 KB, so we
// step quality down until we're comfortably under the limit. Modern browsers
// honor EXIF orientation when drawing into a canvas (Chrome 81+/Safari 13.4+),
// so no extra rotation pass is needed.
async function prepareImageForUpload(file: File): Promise<string> {
  const img = await loadImage(file);
  const { width: w0, height: h0 } = img;
  const scale = Math.min(1, MAX_DIMENSION / Math.max(w0, h0));
  const width = Math.round(w0 * scale);
  const height = Math.round(h0 * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');
  ctx.drawImage(img, 0, 0, width, height);

  for (const q of QUALITY_STEPS) {
    const blob = await canvasToBlob(canvas, q);
    // Base64 inflates by ~33% — server checks decoded bytes, so the blob
    // size is the right thing to gate on.
    if (blob.size <= MAX_DECODED_BYTES) return blobToBase64(blob);
  }
  // Last-ditch: ship the smallest-quality blob anyway. If the server still
  // rejects, the caller surfaces "Carte non reconnue" — same UX as a no-match.
  const fallback = await canvasToBlob(canvas, QUALITY_STEPS[QUALITY_STEPS.length - 1]);
  return blobToBase64(fallback);
}

function narrowConfidence(v: unknown): OcrConfidence {
  return v === 'high' || v === 'medium' ? v : 'low';
}

export async function recognizeRamqFromFile(file: File): Promise<OcrRamqResult> {
  try {
    const image = await prepareImageForUpload(file);
    const res = await apiPost<Partial<OcrRamqResult> & { confidence?: unknown }>(
      'ocr/ramq',
      { image },
      { timeoutMs: 25_000 },
    );
    return {
      ramq: res.ramq ?? '',
      firstName: res.firstName,
      lastName: res.lastName,
      confidence: narrowConfidence(res.confidence),
      message: res.message,
    };
  } catch (e) {
    // Silent catch matches Flutter behavior — caller falls back to manual
    // entry. Log so the failure shows up in DevTools for debugging.
    console.warn('OCR failed:', e);
    return { ramq: '', confidence: 'low' };
  }
}
